import { forwardRef, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { Modal } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AnimatePresence, motion as Motion } from 'framer-motion';

const SLIDE_UP_TRANSITION = { type: 'spring', stiffness: 360, damping: 36 };
const DRAG_CLOSE_OFFSET = 80;
const DRAG_CLOSE_VELOCITY = 500;

const getSlideUpMotionProps = (inProp, onEnter, onExited, onClose) => ({
  initial: { y: '100%' },
  animate: { y: inProp ? 0 : '100%' },
  transition: SLIDE_UP_TRANSITION,
  onAnimationComplete: () => (inProp ? onEnter?.() : onExited?.()),
  drag: inProp ? 'y' : false,
  dragConstraints: { top: 0, bottom: 0 },
  dragElastic: { top: 0, bottom: 0.4 },
  onDragEnd: (_event, info) => {
    if (info.offset.y > DRAG_CLOSE_OFFSET || info.velocity.y > DRAG_CLOSE_VELOCITY) {
      onClose?.();
    }
  }
});

const MotionSlideUpDiv = forwardRef(function MotionSlideUpDiv(
  { in: inProp, onEnter, onExited, onClose, ...rest },
  ref
) {
  return <Motion.div ref={ref} {...getSlideUpMotionProps(inProp, onEnter, onExited, onClose)} {...rest} />;
});

export const KitCustomModal = ({ open, onClose, padding, children }) => createPortal(
  <AnimatePresence>
    {open ? (
      <Fragment key='kit-sheet'>
        <Motion.div
          className='fixed inset-0 z-[1300] bg-black/40'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />
        <Motion.div
          className={`fixed bottom-0 left-0 right-0 z-[1301] overflow-hidden rounded-t-2xl bg-white shadow-[0px_0px_24px_0px_rgba(0,0,0,0.12)] sm:rounded-t-[18px] ${padding ? '' : 'px-0 py-4 sm:p-4'}`}
          style={{
            height: '100dvh',
            maxHeight: '90dvh',
            minHeight: '100px',
            ...(padding ? { padding } : {})
          }}
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={SLIDE_UP_TRANSITION}
          drag='y'
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0, bottom: 0.4 }}
          onDragEnd={(_event, info) => {
            if (info.offset.y > DRAG_CLOSE_OFFSET || info.velocity.y > DRAG_CLOSE_VELOCITY) {
              onClose?.();
            }
          }}
        >
          {children}
        </Motion.div>
      </Fragment>
    ) : null}
  </AnimatePresence>,
  document.body
);

export const CustomSlideModal = ({
  open,
  onClose = () => { },
  padding,
  children,
  className,
  maxWidth,
  radius,
  mobileRadius,
  mobileMaxHeight,
  mobilePadding,
  mobileClassName,
  mobileHeight,
  id,
  outline,
  height
}) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');

  return (
    <Modal
      open={open}
      onClose={e => {
        e.stopPropagation();
        onClose();
      }}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      {isDesktop ? (
        <div className='absolute inset-0 horizontal-center'>
          <div
            onClick={e => e.stopPropagation()}
            id={id}
            className={`bg-white min-h-[100px] w-full max-w-4xl ${className ?? ''}`}
            style={{
              boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.12)',
              borderRadius: radius ? radius : '10px',
              ...(height && { height: height }),
              ...(maxWidth && maxWidth !== 'md' && { maxWidth }),
              padding: padding ? padding : '1rem',
              outline: outline
            }}
          >
            {children}
          </div>
        </div>
      ) : (
        <MotionSlideUpDiv
          in={open}
          onClose={onClose}
          id={id}
          className={`absolute bottom-0 w-full bg-white ${mobileClassName}`}
          style={{
            boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.12)',
            padding: mobilePadding ? mobilePadding : '1rem',
            height: mobileHeight ? mobileHeight : '100dvh',
            maxHeight: mobileMaxHeight ? mobileMaxHeight : '50dvh',
            borderRadius: mobileRadius ? mobileRadius : '1rem 1rem 0 0',
          }}
        >
          {children}
        </MotionSlideUpDiv>
      )}
    </Modal>
  );
};
